//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import { PojoSet } from 'pojo-sets';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FullscreenModal } from '@components/FullscreenModal';
import { Alert } from '@components/atoms/alert/Alert';
import {
  FlatDefaultButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyText,
  CaptionSecondaryText,
  SubsectionText,
  SubtitleText,
  UiCopySecondaryText,
  UiCopyText,
} from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { requestRightsNonMentionedModalActions } from '@galleries/actions/requestRightsNonMentionedModalActions';
import { rightsSkippedModalActions } from '@galleries/actions/rightsSkippedModalActions';
import { MediaCarousel } from '@galleries/components/content/media-carousel/MediaCarousel';
import { PendingIconOverlay } from '@galleries/components/content/pending-icon/PendingIconOverlay';
import RotatingRightsMessages from '@galleries/components/content/rotating-rights-messages/RotatingRightsMessages';
import { WorkflowBreadcrumbs } from '@galleries/components/content/workflow-breadcrumbs/WorkflowBreadcrumbs';
import { RightsSkippedModal } from '@galleries/components/modals/rights/RightsSkippedModal';
import { getNonMentionedItems } from '@galleries/selectors/getNonMentionedItems';
import ItemId from '@models/ItemId';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import AnchorWidget from '@react-bridge/react-ng-widgets/anchor-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export const RequestRightsNonMentioned = () => {
  const dispatch = useDispatch();

  const { isOpen, materializedItems, selectedIndex, rightsPending } =
    useSelector((state) => state.galleries.requestRightsNonMentionedModal);

  const nonMentionedItems = useSelector(getNonMentionedItems);

  const defaultInstagramAccount = useSelector(
    (state) => state.instagram.account.defaultInstagramAccount,
  );

  const { clearSelectedItems } = getBulkActionsActions();

  const {
    closeModal,
    requestRightsWithOauthCheck,
    setSelectedIndexWithinBounds,
  } = requestRightsNonMentionedModalActions;

  const [showNoInstagramAccountReminder, setShowNoInstagramAccountReminder] =
    useState(false);

  const remainingItemsSentence =
    nonMentionedItems.length === 1
      ? `You'll need to manually paste the message on the remaining item.`
      : `You'll need to manually paste the message on each of the remaining ${nonMentionedItems.length} items.`;

  const numRemainingItemsToRequest =
    nonMentionedItems.length - Object.keys(rightsPending).length;

  const onRightsSkipped = () => {
    dispatch(clearSelectedItems());
    dispatch(closeModal());
  };

  const getButtonsRow = () => {
    if (numRemainingItemsToRequest > 0) {
      const onClick = async () => {
        if (isUndefined(defaultInstagramAccount)) {
          setShowNoInstagramAccountReminder(true);
        } else if (isDefined(nonMentionedItems[selectedIndex])) {
          await dispatch(
            requestRightsWithOauthCheck(nonMentionedItems[selectedIndex]),
          );
        }
      };

      return (
        <>
          <RequestLaterButton
            onClick={() =>
              dispatch(rightsSkippedModalActions.openRightsSkippedModal())
            }
          >
            <RequestLaterButtonContent>
              Request Rights Later For&nbsp;
              {pluralize(numRemainingItemsToRequest, 'Item')}
            </RequestLaterButtonContent>
          </RequestLaterButton>
          <Popover
            content={
              <StyledBodyText>
                <MessageCopiedIcon name='circle-checkmark-filled' />
                Message Copied to Clipboard
              </StyledBodyText>
            }
            placement='top'
            shouldOpenOnHover={false}
            target={
              <RequestButton onClick={onClick}>
                <RequestButtonContent>
                  Request Rights on Instagram
                  <SmallLinkout name='linkout' />
                </RequestButtonContent>
              </RequestButton>
            }
          />
        </>
      );
    } else {
      return (
        <PrimaryButton onClick={() => dispatch(closeModal())}>
          Done
        </PrimaryButton>
      );
    }
  };

  const overlaidItems = PojoSet.toArray(rightsPending).reduce<
    Record<ItemId, true>
  >(
    (accumulator, currentValue) => ({ ...accumulator, [currentValue]: true }),
    {},
  );

  return (
    <FullscreenModal
      headerTitle={'Request Rights'}
      isActive={true}
      isOpen={isOpen}
      onClose={() => dispatch(closeModal())}
    >
      <BreadcrumbsContainer>
        <WorkflowBreadcrumbs
          currentIndex={1}
          sectionTitles={['add to galleries', 'request rights']}
        />
      </BreadcrumbsContainer>
      <HeaderContainer>
        <SubsectionText>non @mentions&nbsp;</SubsectionText>
        <UiCopySecondaryText>
          {nonMentionedItems.length} of{' '}
          {pluralize(materializedItems.length, 'item')}
        </UiCopySecondaryText>
      </HeaderContainer>
      <ContentContainer>
        <LeftColumn>
          <MediaCarousel
            contentItems={materializedItems}
            imgOverlay={<PendingIconOverlay />}
            onIndexChange={(index) =>
              dispatch(setSelectedIndexWithinBounds(index))
            }
            overlaidItems={overlaidItems}
            selectedIndex={selectedIndex}
          />
        </LeftColumn>
        <RightColumn>
          <SubtitleText>Choose a Messaging Campaign</SubtitleText>
          <UiCopyText>
            <CTAContainer>
              Choose a messaging campaign to use. Click the button below to
              automatically copy your message and open the post in Instagram.
              {remainingItemsSentence}
            </CTAContainer>
          </UiCopyText>
          <StyledContainer>
            Messaging Campaign
            <SmallHelp name='help-lg' />
          </StyledContainer>
          <StyledRotatingRightsMessages
            selectedItem={nonMentionedItems[selectedIndex]}
          />
          {!defaultInstagramAccount && (
            <StyledAlert type='error'>
              Your Instagram account is not connected. You must be connected to
              request rights.
              <AnchorWidget
                state='account-settings.social'
                text='Click here to connect your Instagram account'
              />
            </StyledAlert>
          )}
          <ButtonRowContainer>{getButtonsRow()}</ButtonRowContainer>
          {showNoInstagramAccountReminder && (
            <ReminderContainer>
              <CaptionSecondaryText>
                <NoIgAccountReminderText>
                  You must connect your Instagram account to request rights.
                </NoIgAccountReminderText>
              </CaptionSecondaryText>
            </ReminderContainer>
          )}
        </RightColumn>
      </ContentContainer>
      <RightsSkippedModal onConfirm={onRightsSkipped} />
    </FullscreenModal>
  );
};

const LeftColumn = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[3]};
  margin-right: ${theme.spacings[5]};
  width: 60%;
`,
);

const RightColumn = styled.div(
  ({ theme }) => `
  margin-left: ${theme.spacings[4]};
  width: 40%;
`,
);

const NoIgAccountReminderText = styled.p(
  ({ theme }) => `
  && {
    color: ${theme.colors.red8};
  }
`,
);

const CTAContainer = styled.p(
  ({ theme }) => `
  && {
    line-height: 1.5;
    margin-top: ${theme.spacings[3]};
  }
`,
);

const SmallHelp = styled(Icon)(
  ({ theme }) => `
  && {
    width: 15px;
    height: 15px;
    margin-left: ${theme.spacings[1]};
  }
`,
);

const SmallLinkout = styled(Icon)(
  ({ theme }) => `
  && {
    width: 12px;
    height: 12px;
    margin-left: ${theme.spacings[1]};
  }
`,
);

const MessageCopiedIcon = styled(Icon)(
  ({ theme }) => `
  && {
    margin-right: ${theme.spacings[2]};
    color: ${theme.colors.green6};
  }
`,
);

const StyledBodyText = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  padding: ${theme.spacings[2]};
`,
);

const BreadcrumbsContainer = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[4]};
`,
);

const HeaderContainer = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[5]};
`,
);

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledRotatingRightsMessages = styled(RotatingRightsMessages)(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]};
`,
);

const StyledAlert = styled(Alert)(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]};
`,
);

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonRowContainer = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: end;
  margin-top: ${theme.spacings[2]};
  margin-bottom: ${theme.spacings[2]};
`,
);

const ReminderContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const RequestLaterButton = styled(FlatDefaultButton)`
  display: flex;
  align-items: center;
  height: 100%;
`;

const RequestLaterButtonContent = styled.div`
  display: flex;
  align-items: center;
  text-overflow: unset;
  word-break: normal;
`;

const RequestButton = styled(PrimaryButton)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  height: 100%;
  margin-left: ${theme.spacings[2]};
`,
);

const RequestButtonContent = styled.div`
  display: flex;
  align-items: center;
  word-break: normal;
`;

angular.module('curalate').component('requestRightsNonMentioned', {
  controller: makeAngularReactController(<RequestRightsNonMentioned />),
});
